import { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Card, CardBody, Col, Container, Row } from "reactstrap";
import { DateObject } from "react-multi-date-picker";
import styled from "styled-components";
import * as XLSX from "xlsx";
import { SELECT_DATES } from "../../../../utils/Constant";
import { useAppDispatch, useAppSelector } from "../../../../ReduxToolkit/Hooks";
import {
  closeModal,
  openModal,
} from "../../../../ReduxToolkit/Slice/UserProfileDetailsModalSlice";
import { useModalSelector } from "../../../../hooks/useModalSelector";
import {
  useTeamMembersListQuery,
  useTeamMembersConveyanceListQuery,
  useTeamMembersConveyanceActionMutation,
} from "../../../../ReduxToolkit/api/teamMembers";
import { useGetEmployeesListQuery } from "../../../../ReduxToolkit/api/employee";
import { useEmployeeProfile } from "../../../../hooks/useEmployeeProfile";
import { showApiResultAlert } from "../../../../utils/AlertManager/showApiAlert";
import {
  IAccountConveyanceReportResposne,
  ITeamMembersConveyanceList,
} from "../../../../Types/TeamMembers";
import ConveyanceInfoModal from "./Modal/ConveyanceInfoModal";
import RangePickerIconTrigger from "../../../../CommonElements/RangePickerIconTrigger";
import ConfirmDeleteDialog from "../../../../CommonElements/ConfirmDeleteDialog";
import { IConveyanceInfoKeys, IConveyanceSelectedRows } from "../types";
import BulkActionBar from "../../../../CommonElements/BulkActionBar";
import { Btn, H6 } from "../../../../AbstractElements";
import { Nullable } from "../../../../Types/Common";
import {
  buildConveyanceApprovalPayload,
  getMonthRange,
  summarizeByMember,
} from "../util/teamMembersConveyanceUtil";
import ConveyanceSummaryList, { ConveyanceSummary } from "./ConveyanceSummary";
import SearchableSelect from "../../../../CommonElements/SearchableSelect";
import SearchableMultiSelect, {
  SearchableMultiSelectOption,
} from "../../../../CommonElements/SearchableMultiSelect";
import { skipToken } from "@reduxjs/toolkit/query";
import TeamMembersConveyanceTable from "./table/TeamMembersConveyanceTable";

type State = {
  dateRange: (DateObject | null)[];
  selectedUser: Array<{ name: string; value: number }>;
  pageNo: number;
  pageSize: number;
  status: string;
};
type Action =
  | { type: "SET_DATE_RANGE"; payload: (DateObject | null)[] }
  | { type: "CLEAR_DATE_RANGE" }
  | { type: "SET_USER"; payload: Array<{ name: string; value: number }> }
  | { type: "SET_PAGE"; payload: number }
  | { type: "SET_PAGE_SIZE"; payload: number }
  | { type: "SET_STATUS"; payload: string }
  | { type: "RESET"; payload: State };

const getInitialState = (
  loginRoleType: string,
  selectedEmployeeId?: string
): State => {
  return {
    dateRange: getMonthRange(),
    selectedUser: selectedEmployeeId
      ? [{ name: selectedEmployeeId, value: Number(selectedEmployeeId) }]
      : [],
    pageNo: 1,
    pageSize: 50,
    status: selectedEmployeeId
      ? "approved"
      : "pending",
  };
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "SET_DATE_RANGE":
      return { ...state, dateRange: action.payload };
    case "CLEAR_DATE_RANGE":
      return { ...state, dateRange: [null, null] };
    case "SET_USER":
      return { ...state, selectedUser: action.payload };
    case "SET_PAGE":
      return { ...state, pageNo: action.payload };
    case "SET_PAGE_SIZE":
      return { ...state, pageSize: action.payload };
    case "SET_STATUS":
      return { ...state, status: action.payload };
    case "RESET":
      return action.payload;
    default:
      return state;
  }
};

export type ConveyanceRow =
  | ITeamMembersConveyanceList
  | IAccountConveyanceReportResposne;

const summaryizeByAccount = (data: IAccountConveyanceReportResposne[]) => {
  return data.map((d) => ({
    id: d.employee_id,
    name: d.employee_name,
    totalAmount: d.total_amount,
  }));
};

type Role = "account" | "manager" | "hr";
type Status = "pending" | "approved" | "rejected";

const ACCOUNT_CONVEYANCE_EXPORT_HEADERS = [
  "Employee Name",
  "Location",
  "Department",
  "Conveyance Date",
  "Submitted On",
  "Reason",
  "Travel From",
  "Travel To",
  "Travel Mode",
  "Distance",
  "Amount",
];

const formatExportDate = (value?: string | null) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  const parts = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).formatToParts(date);

  const day = parts.find((part) => part.type === "day")?.value ?? "";
  const month = parts.find((part) => part.type === "month")?.value ?? "";
  const year = parts.find((part) => part.type === "year")?.value ?? "";

  return [day, month, year].filter(Boolean).join(" ");
};

const buildAccountConveyanceExportRows = (
  rows: IAccountConveyanceReportResposne[]
) =>
  rows.flatMap((row) => {
    const detailRows = (row.details ?? []).map((detail) => ({
      "Employee Name": row.employee_name ?? "",
      Location: row.location ?? "",
      Department: row.department ?? "",
      "Conveyance Date": formatExportDate(detail.date),
      "Submitted On": formatExportDate(detail.submitted_on),
      Reason: detail.reason ?? "",
      "Travel From": detail.from ?? "",
      "Travel To": detail.to ?? "",
      "Travel Mode": detail.mode ?? "",
      Distance: detail.distance ?? "",
      Amount: detail.approved_amount ?? "",
    }));

    return [
      ...detailRows,
      {
        "Employee Name": "",
        Location: "",
        Department: "",
        "Conveyance Date": "",
        "Submitted On": "",
        Reason: "",
        "Travel From": "",
        "Travel To": "",
        "Travel Mode": "",
        Distance: "",
        Amount: `Total Amount ${row.employee_name ?? ""}: ${
          row.total_amount ?? 0
        }`,
      },
    ];
  });

const FilterCard = styled(Card)`
  overflow: visible;
`;

const FilterBody = styled(CardBody)`
  padding-bottom: 18px;
`;

const FilterRow = styled(Row)`
  align-items: flex-end;
  row-gap: 12px;
`;

const FilterField = styled.div`
  width: 100%;

  label,
  .form-label {
    display: block;
    min-height: 14px;
    margin: 0 0 6px 0 !important;
    color: #64748b;
    font-size: 11px !important;
    font-weight: 500;
    line-height: 14px;
  }

  .input-group {
    display: block;
  }

  .flatpicker-calender {
    display: block;
    width: 100%;
  }

  .flatpicker-calender > label {
    font-size: 11px !important;
  }

  button,
  select,
  input.form-control {
    min-height: 34px;
  }
`;

const TeamMembersConveyanceContainer = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id: routeEmployeeId } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const { employeeProfileDetails, loggedInEmployee, loggedInEmployeeRole } =
    useEmployeeProfile();

  const [state, localDispatch] = useReducer(
    reducer,
    getInitialState("manager", routeEmployeeId)
  );

  const [selectConveyanceInfoView, setSelectConveyanceInfoView] =
    useState<IConveyanceInfoKeys>();

  const [selectConveyance, setSelectConveyance] = useState<
    ITeamMembersConveyanceList[] | IAccountConveyanceReportResposne[]
  >();

  const [conveyanceType, setConveyanceType] = useState<"Approve" | "Reject">(
    "Approve"
  );

  const [isClearedSelectedRows, setIsClearedSelectedRows] =
    useState<boolean>(false);

  const [selectConveyanceRows, setSelectConveyanceRows] =
    useState<Nullable<IConveyanceSelectedRows>>(null);

  const [selectedRowKeys, setSelectedRowKeys] = useState<(string | number)[]>(
    []
  );

  const [groupConveyance, setGroupConveyance] = useState<ConveyanceSummary[]>();

  const { darkMode } = useAppSelector(({ layout }) => layout);
  const { isOpen, modalType } = useModalSelector();

  const employeeId = employeeProfileDetails?.jobInformation?.userId;
  const isEmployeeProfilePath = location.pathname.includes(
    "/user-management/user"
  );
  const isAccountConveyanceDetailsPath =
    location.pathname.includes("/account-zone/conveyance/") &&
    !!routeEmployeeId;
  const isHrApprovedConveyancePath = location.pathname.includes(
    "/hr-zone/employee-conveyance-approved"
  );
  const isHrEmployeeConveyancePath =
    location.pathname === "/hr-zone/employee-conveyance" ||
    isHrApprovedConveyancePath;
  const isHrApprovedConveyanceDetailsPath =
    isHrApprovedConveyancePath && !!routeEmployeeId;
  const isAccountStyleDetailsPath =
    isAccountConveyanceDetailsPath || isHrApprovedConveyanceDetailsPath;
  const effectiveConveyanceStatus = isHrApprovedConveyancePath
    ? "approved"
    : state.status;
  const effectiveConveyanceType = (() => {
    if (location.pathname.includes("/account-zone/conveyance")) {
      return "account";
    }

    if (location.pathname === "/hr-zone/employee-conveyance") {
      return "hr";
    }

    if (location.pathname.includes("/members/conveyance")) {
      return "manager";
    }

    if (isHrApprovedConveyancePath) {
      return "account";
    }

    return loggedInEmployeeRole;
  })();

  // Fetch team members (dropdown)
  const {
    data: teamMembersList,
    isLoading: isTeamMembersListLoading,
    isError: isTeamMembersListError,
    error: teamMembersListError,
  } = useTeamMembersListQuery(
    isEmployeeProfilePath && employeeId ? employeeId : undefined,
    { skip: isHrEmployeeConveyancePath }
  );

  const {
    data: employeesList,
    isLoading: isEmployeesListLoading,
    isError: isEmployeesListError,
    error: employeesListError,
  } = useGetEmployeesListQuery(
    isHrEmployeeConveyancePath
      ? {
          manager: false,
          status: 1,
          size: 1000,
        }
      : skipToken
  );

  // Fetch conveyance data using RTK Query (instead of thunk)
  const hasDateRange = !!(state.dateRange[0] && state.dateRange[1]);
  const effectiveEmployeeId = isEmployeeProfilePath
    ? employeeProfileDetails?.jobInformation?.userId
    : loggedInEmployee?.id;

  const {
    data: conveyanceData,
    isLoading: isConveyanceReportListLoading,
    isFetching: isConveyanceReportFetching,
    isError: isConveyanceReportError,
    error: conveyanceError,
  } = useTeamMembersConveyanceListQuery(
    hasDateRange && effectiveEmployeeId
      ? {
          employeeId: String(effectiveEmployeeId),
          startDate: state.dateRange[0]?.format("YYYY-MM-DD"),
          endDate: state.dateRange[1]?.format("YYYY-MM-DD"),
          ...(state.selectedUser?.length > 0 && {
            selectEmployeeId: state.selectedUser
              .map((user) => user.value)
              .join(","),
          }),
          page: state.pageNo,
          size: state.pageSize,
          type: effectiveConveyanceType,
          ...(effectiveConveyanceStatus !== "select" && {
            status: effectiveConveyanceStatus,
          }),
        }
      : skipToken
  );

  const [teamMembersConveyanceAction, { isLoading }] =
    useTeamMembersConveyanceActionMutation();

  const role = loggedInEmployeeRole as Role;
  const status = effectiveConveyanceStatus as Status;
  const canManagerOrHrReview = ["manager", "hr"].includes(role);

  const showBtnSecond = role === "account" ? false : true;

  const showBtnInitial = true;

  const btnLabel =
    role === "account"
      ? "Paid"
      : canManagerOrHrReview
      ? "Approve"
      : "";

  const resetState = () => {
    setSelectConveyanceRows(null);
    setGroupConveyance([]);
    setSelectConveyance([]);
    setIsClearedSelectedRows((prev) => !prev);
    setSelectedRowKeys([]);
  };

  // Team members dropdown options
  const teamMemberListOptions = useMemo(
    () =>
      (teamMembersList?.data?.team_members ?? []).map(({ id, name, email }) => ({
        label: name,
        value: id,
        description: email ?? `Employee ID: ${id}`,
      })),
    [teamMembersList]
  );

  const employeeListOptions = useMemo(
    () =>
      (employeesList?.data ?? []).map(({ id, name, email }) => ({
        label: name,
        value: id,
        description: email ?? `Employee ID: ${id}`,
      })),
    [employeesList]
  );

  const teamMembersOptions = isHrEmployeeConveyancePath
    ? employeeListOptions
    : teamMemberListOptions;

  const selectedTeamMemberOptions = useMemo(
    () =>
      state.selectedUser.map((user) => ({
        label: user.name,
        value: user.value,
      })),
    [state.selectedUser]
  );

  const selectedRouteEmployeeName = useMemo(() => {
    if (!routeEmployeeId || !conveyanceData?.data?.length) return "";

    const accountRow = (
      conveyanceData.data as IAccountConveyanceReportResposne[]
    ).find((row) => String(row.employee_id) === routeEmployeeId);

    return (
      accountRow?.employee_name ??
      (conveyanceData.data[0] as IAccountConveyanceReportResposne | undefined)
        ?.employee_name ??
      ""
    );
  }, [conveyanceData?.data, routeEmployeeId]);

  const statusOptions = useMemo(
    () => [
      {
        label: "Pending",
        value: "pending",
        description: "Waiting for action",
      },
      {
        label: loggedInEmployeeRole === "account" ? "Paid" : "Approved",
        value: "approved",
        description:
          loggedInEmployeeRole === "account"
            ? "Marked as paid"
            : "Approved conveyance",
      },
      ...(loggedInEmployeeRole !== "account"
        ? [
            {
              label: "Rejected",
              value: "rejected",
              description: "Rejected conveyance",
            },
          ]
        : []),
    ],
    [loggedInEmployeeRole]
  );

  const conveyanceInfoTitle = useMemo(() => {
    return `${selectConveyanceInfoView?.teamMember.name} - ${selectConveyanceInfoView?.teamMember.email} - ${selectConveyanceInfoView?.id}`;
  }, [selectConveyanceInfoView]);

  const dialogLabel = useMemo(() => {
    if (
      selectConveyanceRows?.selectedCount &&
      selectConveyanceRows.selectedCount > 1
    ) {
      return `Review & Approve Conveyance`;
    }

    const firstItem = selectConveyance?.[0];
    if (!firstItem) return "";

    if ("conveyance_date" in firstItem) {
      return `Are you sure you want to ${conveyanceType.toLowerCase()} the conveyance of ${
        firstItem.team_member?.name ?? "this member"
      } - ${firstItem.conveyance_date}?`;
    }

    return `Are you sure you want to ${conveyanceType.toLowerCase()} this account conveyance record?`;
  }, [selectConveyanceRows, selectConveyance, conveyanceType]);

  const dialogDescription = useMemo(
    () => (
      <ConveyanceSummaryList
        data={groupConveyance as ConveyanceSummary[]}
        isShowApprovedAmount={loggedInEmployeeRole === "account" ? false : true}
        isShowTotalConveyance={
          loggedInEmployeeRole === "account" ? false : true
        }
      />
    ),
    [groupConveyance]
  );

  const bulkActionLabel = useMemo(() => {
    return `You have selected: ${selectConveyanceRows?.selectedCount}`;
  }, [selectConveyanceRows]);

  const handleCloseModal = useCallback(
    () => dispatch(closeModal()),
    [dispatch]
  );

  // Date range handler
  const handleDateRange = useCallback((range: (DateObject | null)[]) => {
    localDispatch({ type: "SET_DATE_RANGE", payload: range });
    localDispatch({ type: "SET_PAGE", payload: 1 });
    resetState();
  }, []);

  const handleClearDateRange = () => {
    localDispatch({ type: "SET_DATE_RANGE", payload: [null, null] });
    resetState();
  };

  const handleSelectEmployee = useCallback(
    (values: SearchableMultiSelectOption[]) => {
      const selectedUsers = values.map((option) => ({
        name: option.label,
        value: Number(option.value),
      }));

      localDispatch({ type: "SET_USER", payload: selectedUsers });
      localDispatch({ type: "SET_PAGE", payload: 1 });
      resetState();

      if (location.pathname.includes("/account-zone/conveyance")) {
        const selectedEmployeeId = selectedUsers[0]?.value;
        navigate(
          selectedEmployeeId
            ? `/account-zone/conveyance/${selectedEmployeeId}`
            : "/account-zone/conveyance"
        );
      } else if (isHrApprovedConveyancePath) {
        const selectedEmployeeId = selectedUsers[0]?.value;
        navigate(
          selectedEmployeeId
            ? `/hr-zone/employee-conveyance-approved/${selectedEmployeeId}`
            : "/hr-zone/employee-conveyance-approved"
        );
      }
    },
    [isHrApprovedConveyancePath, location.pathname, navigate]
  );
  const handlePageChange = useCallback((newPage: number) => {
    localDispatch({ type: "SET_PAGE", payload: newPage });
    resetState();
  }, []);

  const handleChangePageSize = useCallback((newSize: number) => {
    localDispatch({ type: "SET_PAGE_SIZE", payload: newSize });
    localDispatch({ type: "SET_PAGE", payload: 1 });
    resetState();
  }, []);

  const handleChangeStatus = useCallback(
    (value: string | number) => {
      if (isHrApprovedConveyancePath) return;
      localDispatch({ type: "SET_STATUS", payload: String(value) });
      resetState();
    },
    [dispatch, isHrApprovedConveyancePath]
  );

  const handleExportConveyanceList = useCallback(() => {
    const exportRows = buildAccountConveyanceExportRows(
      (conveyanceData?.data ?? []) as IAccountConveyanceReportResposne[]
    );

    if (!exportRows.length) {
      alert("No data to export");
      return;
    }

    const worksheet = XLSX.utils.json_to_sheet(exportRows, {
      header: ACCOUNT_CONVEYANCE_EXPORT_HEADERS,
    });
    worksheet["!cols"] = [
      { wch: 24 },
      { wch: 16 },
      { wch: 20 },
      { wch: 16 },
      { wch: 16 },
      { wch: 38 },
      { wch: 20 },
      { wch: 20 },
      { wch: 14 },
      { wch: 10 },
      { wch: 30 },
    ];
    Object.keys(worksheet).forEach((cellKey) => {
      if (cellKey.startsWith("!")) return;
      worksheet[cellKey].s = {
        alignment: { vertical: "top", wrapText: true },
      };
    });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Conveyance");
    XLSX.writeFile(
      workbook,
      `conveyance-approved-${state.dateRange[0]?.format(
        "YYYY-MM-DD"
      )}-${state.dateRange[1]?.format("YYYY-MM-DD")}.xlsx`,
      { bookType: "xlsx" }
    );
  }, [conveyanceData?.data, state.dateRange]);

  const handleOpenDetailsConveyance = useCallback(
    (info: ITeamMembersConveyanceList) => {
      const approvalHistory =
        info.last_approval?.map((approval) => ({
          by: approval.by ?? "",
          date: approval.date ?? "",
          remark: approval.remark ?? "",
          status: approval.status ?? "",
          approverInfo: {
            department: approval.approver_info?.department ?? "",
            designation: approval.approver_info?.designation ?? "",
            email: approval.approver_info?.email ?? "",
            id: approval.approver_info?.id ?? "",
            name: approval.approver_info?.name ?? "",
            location: approval.approver_info?.location ?? "",
          },
        })) ?? [];
      const lastApproval = approvalHistory[approvalHistory.length - 1];

      setSelectConveyanceInfoView({
        id: info.conveyance_code ?? "",
        date: info.conveyance_date ?? "",
        submitOn: info.submitted_on ?? "",
        travelFrom: info.travelfrom ?? "",
        travelTo: info.travel_to ?? "",
        mode: info.mode ?? "",
        distance: info.distance ?? "",
        amount: info.amount ?? "",
        approvedAmount: Number(info.approved_amount) ?? null,
        reason: info.reason ?? "",
        status: info.status ?? "",
        teamMember: {
          name: info.team_member?.name ?? "",
          email: info.team_member?.email ?? "",
        },
        department: info?.team_member.department ?? "",
        designation: info?.team_member?.designation ?? "",
        center: info?.team_member?.location ?? "",
        attachment: info.bill ?? null,
        lastApproval,
        lastApprovals: approvalHistory,
      });
      dispatch(openModal({ modalType: "conveyanceInfoModal" }));
    },
    [dispatch, conveyanceData?.data]
  );

  const handleOpenConveyanceDialog = useCallback(
    (
      conveyanceType: "Approve" | "Reject",
      conveyaneInfo: ITeamMembersConveyanceList | IAccountConveyanceReportResposne
    ) => {
      setConveyanceType(conveyanceType);
      setSelectConveyance(
        [conveyaneInfo] as
          | ITeamMembersConveyanceList[]
          | IAccountConveyanceReportResposne[]
      );
      setGroupConveyance(
        "details" in conveyaneInfo
          ? summaryizeByAccount([conveyaneInfo])
          : summarizeByMember([conveyaneInfo])
      );
      dispatch(openModal({ modalType: "approvalConveyanceDialog" }));
    },
    [dispatch]
  );

  const handleRowSelected = useCallback(
    (values: IConveyanceSelectedRows) => {
      const summary =
        loggedInEmployeeRole === "account"
          ? summaryizeByAccount(
              values.selectedRows as IAccountConveyanceReportResposne[]
            )
          : summarizeByMember(
              values.selectedRows as ITeamMembersConveyanceList[]
            );
      setSelectConveyanceRows(values);
      setGroupConveyance(summary);
      setSelectConveyance(values.selectedRows as ITeamMembersConveyanceList[]);
    },
    [dispatch, loggedInEmployeeRole]
  );

  const handleBulkApproved = useCallback(() => {
    setConveyanceType("Approve");
    dispatch(openModal({ modalType: "approvalConveyanceDialog" }));
  }, []);

  const handleBulkRejected = useCallback(() => {
    setConveyanceType("Reject");
    dispatch(openModal({ modalType: "approvalConveyanceDialog" }));
  }, []);

  const handleConveyanceAction = useCallback(async () => {
    if (!selectConveyance) return;
    const payload = buildConveyanceApprovalPayload(
      selectConveyance,
      conveyanceType,
      loggedInEmployeeRole
    );
    const result = await teamMembersConveyanceAction(payload);
    dispatch(closeModal());
    setSelectConveyanceRows(null);
    showApiResultAlert(result, {
      title: `Hey! ${loggedInEmployee?.name} Info`,
      source: "",
    });
  }, [
    selectConveyance,
    conveyanceType,
    loggedInEmployeeRole,
    dispatch,
    teamMembersConveyanceAction,
  ]);

  useEffect(() => {
    if (loggedInEmployeeRole) {
      const freshState = getInitialState(loggedInEmployeeRole, routeEmployeeId);
      if (isHrApprovedConveyancePath) {
        freshState.status = "approved";
      }
      localDispatch({ type: "RESET", payload: freshState } as any);
    }
  }, [loggedInEmployeeRole, routeEmployeeId, isHrApprovedConveyancePath]);

  useEffect(() => {
    if (!routeEmployeeId) return;

    const selectedEmployee = teamMembersOptions.find(
      (option) => String(option.value) === routeEmployeeId
    );

    localDispatch({
      type: "SET_USER",
      payload: [
        {
          name:
            selectedEmployee?.label || selectedRouteEmployeeName || routeEmployeeId,
          value: Number(routeEmployeeId),
        },
      ],
    });
  }, [routeEmployeeId, selectedRouteEmployeeName, teamMembersOptions]);

  useEffect(() => {
    const shouldRun =
      isTeamMembersListError || isEmployeesListError || isConveyanceReportError;

    const errors = teamMembersListError || employeesListError || conveyanceError;

    if (shouldRun) {
      showApiResultAlert(errors, { title: `Hey ${loggedInEmployee?.name}!` });
    }
  }, [isTeamMembersListError, isEmployeesListError, isConveyanceReportError]);

  return (
    <Container fluid>
      <FilterCard>
        <FilterBody>
          <FilterRow>
            <Col md="4">
              <FilterField>
                <RangePickerIconTrigger
                  darkMode={darkMode}
                  dateRange={state.dateRange}
                  handleDateRange={handleDateRange}
                  SELECT_DATES={SELECT_DATES}
                  handleClearDateRange={handleClearDateRange}
                />
              </FilterField>
            </Col>
            <Col md="3">
              <FilterField>
                <SearchableMultiSelect
                  label="Users"
                  name="team-member"
                  multiple={false}
                  value={selectedTeamMemberOptions}
                  onChange={handleSelectEmployee}
                  options={teamMembersOptions}
                  placeholder="Select users"
                  searchPlaceholder={
                    isEmployeesListLoading ? "Loading users..." : "Search users..."
                  }
                  disabled={
                    isHrEmployeeConveyancePath
                      ? isEmployeesListLoading
                      : isTeamMembersListLoading
                  }
                />
              </FilterField>
            </Col>
            <Col md="3">
              <FilterField>
                <SearchableSelect
                  label="Status"
                  name="status"
                  value={effectiveConveyanceStatus}
                  options={statusOptions}
                  onChange={handleChangeStatus}
                  placeholder="Select status"
                  disabled={isHrApprovedConveyancePath}
                />
              </FilterField>
            </Col>
            {isHrApprovedConveyancePath && (
              <Col md="2">
                <FilterField>
                  <Btn
                    color="primary"
                    className="d-inline-flex align-items-center gap-2"
                    disabled={!conveyanceData?.data?.length}
                    onClick={handleExportConveyanceList}
                  >
                    <i className="icofont icofont-download" />
                    Export
                  </Btn>
                </FilterField>
              </Col>
            )}
          </FilterRow>
        </FilterBody>
      </FilterCard>

      {/* Table */}
      <Card className="mt-4">
        <CardBody>
          <H6 className="mb-2 f-16 fw-semibold">
            {location.pathname.includes("/members/conveyance")
              ? "Team Members Conveyance Report"
              : "Conveyance Report"}
          </H6>
          <TeamMembersConveyanceTable
            data={conveyanceData!}
            loginEmployeeRole={loggedInEmployeeRole}
            pageSize={state.pageSize}
            onPageChange={handlePageChange}
            onRowsPageChange={handleChangePageSize}
            conveyanceReportLoading={
              isConveyanceReportListLoading || isConveyanceReportFetching
            }
            openConveyanceDetails={handleOpenDetailsConveyance}
            openConveyanceDialog={handleOpenConveyanceDialog}
            onRowSelected={handleRowSelected}
            conveyanceStatus={effectiveConveyanceStatus}
            isClearSelectedRows={isClearedSelectedRows}
            selectedRowKeys={selectedRowKeys}
            setSelectedRowKeys={setSelectedRowKeys}
            filterStatus={effectiveConveyanceStatus}
            useManagerColumns={isAccountStyleDetailsPath}
            useAccountColumns={isHrApprovedConveyancePath && !routeEmployeeId}
            hideActionButtons={isAccountStyleDetailsPath}
            disableSelection={isHrApprovedConveyancePath}
            detailsBasePath={
              isHrApprovedConveyancePath
                ? "/hr-zone/employee-conveyance-approved"
                : "/account-zone/conveyance"
            }
          />
          {selectConveyanceRows &&
            !isAccountStyleDetailsPath &&
            !isHrApprovedConveyancePath &&
            selectConveyanceRows?.selectedCount > 0 &&
            selectConveyanceRows?.selectedRows.length > 0 && (
              <BulkActionBar
                className="end-0 pe-4 mt-2"
                label={bulkActionLabel}
                selectedRows={selectConveyanceRows?.selectedRows.length}
                onApprove={handleBulkApproved}
                onReject={handleBulkRejected}
                showBtnSecond={showBtnSecond}
                showBtnInitial={showBtnInitial}
                btnLabel={btnLabel}
              />
            )}
        </CardBody>
      </Card>
      {modalType === "conveyanceInfoModal" && selectConveyanceInfoView && (
        <ConveyanceInfoModal
          isOpen={isOpen}
          onClose={handleCloseModal}
          title={conveyanceInfoTitle}
          conveyanceInfoDetails={selectConveyanceInfoView}
        />
      )}
      {modalType === "approvalConveyanceDialog" && (
        <ConfirmDeleteDialog
          size="lg"
          isOpen={isOpen}
          firstBtnLabel="Yes"
          secondBtnLabel="No"
          loading={isLoading}
          label={dialogLabel}
          showCloseButton={false}
          description={dialogDescription}
          handleToggle={handleCloseModal}
          handleClickSecondBtn={handleCloseModal}
          handleClickFirstBtn={handleConveyanceAction}
          loadingText="Please wait we are updating the team members conveyance"
        />
      )}
    </Container>
  );
};

export default TeamMembersConveyanceContainer;


